import type { NavItem } from './routes.ts';
import { siteHeaderMarkup } from './shellMarkup.ts';

export interface Shell {
  readonly main: HTMLElement;
  setActive(path: string): void;
  setChromeHidden(hidden: boolean): void;
}

/** Shared keyboard, dropdown and mobile-menu behavior, including SSR pages. */
export function bindShellNavigation(header: HTMLElement): () => void {
  // Older profile-service HTML can outlive a static website release.
  header.querySelector('.site-search-toggle')?.remove();
  const nav = header.querySelector<HTMLElement>('.site-nav')!;
  const menuButton = nav.querySelector<HTMLButtonElement>('.site-menu-toggle')!;
  const navLinks = nav.querySelector<HTMLElement>('.site-nav-links')!;
  const closeMenu = (): void => {
    nav.classList.remove('menu-open');
    menuButton.setAttribute('aria-expanded', 'false');
    for (const details of navLinks.querySelectorAll('details')) details.open = false;
  };
  menuButton.addEventListener('click', () => {
    const open = !nav.classList.contains('menu-open');
    closeMenu();
    nav.classList.toggle('menu-open', open);
    menuButton.setAttribute('aria-expanded', String(open));
  });
  nav.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const wasMobileOpen = nav.classList.contains('menu-open');
    const summary = navLinks.querySelector<HTMLElement>('details[open] summary');
    closeMenu();
    if (wasMobileOpen) menuButton.focus(); else summary?.focus();
  });
  document.addEventListener('click', event => {
    if (event.target instanceof Node && !nav.contains(event.target)) closeMenu();
  });
  nav.addEventListener('focusout', event => {
    if (event.relatedTarget instanceof Node && !nav.contains(event.relatedTarget)) closeMenu();
  });
  for (const link of nav.querySelectorAll('a')) link.addEventListener('click', closeMenu);
  for (const details of nav.querySelectorAll('details')) {
    details.addEventListener('toggle', () => details.querySelector('summary')?.setAttribute('aria-expanded', String(details.open)));
  }
  return closeMenu;
}

export function createShell(root: HTMLElement, navItems: readonly NavItem[]): Shell {
  root.innerHTML = siteHeaderMarkup(navItems);
  const header = root.querySelector<HTMLElement>('.site-header')!;
  const closeMenu = bindShellNavigation(header);
  const main = document.createElement('main');
  main.id = 'site-main';
  root.append(main);
  return {
    main,
    setActive(path): void {
      closeMenu();
      for (const link of header.querySelectorAll('a')) {
        if (link.classList.contains('site-brand')) continue;
        const active = link.getAttribute('href') === path;
        link.classList.toggle('active', active);
        if (active) link.setAttribute('aria-current', 'page'); else link.removeAttribute('aria-current');
      }
      for (const group of header.querySelectorAll('details')) {
        group.querySelector('summary')?.classList.toggle('active', Boolean(group.querySelector('a.active')));
      }
    },
    setChromeHidden(hidden): void { root.classList.toggle('route-play', hidden); },
  };
}
